import { DocumentType } from '@repo/shared/dtos/document'
import { Button } from '@repo/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form'
import { useFormValidator, useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { Trash2Icon, User2Icon } from 'lucide-react'
import { ReactNode, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useAbility } from '@/ability/ability-context'
import { CaseWorkerListWrapper } from '@/components/beneficiary/CaseWorkerList'
import { ResetAndUpdate } from '@/components/ResetAndUpdate'
import { useView } from '@/hooks'
import { AvailableParams } from '@/routes/router'
import { storeActions } from '@/store/actions'
import { Beneficiary } from '@/store/beneficiary'
import { useStoreDispatch } from '@/store/hooks'
import { beneficiarySchema } from '@/validation-schemas'

const keyPrefix = 'beneficiary.editor'

export function BeneficiaryEditor({
  beneficiary,
  onOpen,
}: {
  beneficiary: Beneficiary
  onOpen: (open: boolean) => void
}) {
  const { tc } = useT()
  const [open, setOpen] = useState(false)
  const handleOpenChange = (open: boolean) => {
    setOpen(open)
    onOpen(open)
  }
  return (
    <>
      <TooltipWrapper side="bottom" content={tc('edit', { cfl: true })}>
        <Button variant="outline" onClick={() => handleOpenChange(true)}>
          <User2Icon className="size-4" /> {tc('edit', { cfl: true })}
        </Button>
      </TooltipWrapper>
      {open && (
        <Modal
          editableBeneficiary={beneficiary}
          modalHandler={{ open, setOpen: handleOpenChange }}
        />
      )}
    </>
  )
}

function Modal({
  editableBeneficiary,
  modalHandler,
}: {
  editableBeneficiary: Beneficiary
  modalHandler: { open: boolean; setOpen: (v: boolean) => void }
}) {
  const { t } = useT({ keyPrefix })
  return (
    <Dialog open={modalHandler.open} onOpenChange={modalHandler.setOpen}>
      <DialogContent size="wide" className="max-h-[75vh]" disableOutsideClick>
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>
        <BeneficiaryForm
          editableBeneficiary={editableBeneficiary}
          onSubmitted={() => modalHandler.setOpen(false)}
        >
          <CaseWorkerListWrapper
            beneficiaryId={editableBeneficiary.id}
            assignedCaseWorkerIds={editableBeneficiary.caseWorkerIds}
          />
        </BeneficiaryForm>
      </DialogContent>
    </Dialog>
  )
}

function BeneficiaryForm({
  editableBeneficiary,
  onSubmitted,
  children,
}: {
  editableBeneficiary: Beneficiary
  onSubmitted: () => void
  children: ReactNode
}) {
  const dispatch = useStoreDispatch()
  const { organizationId } = useParams<AvailableParams>()
  if (!organizationId) return null

  const { t, tc } = useT({ keyPrefix })

  const defaultValues = useMemo(
    () => ({ fullName: editableBeneficiary.fullName }),
    [editableBeneficiary],
  )

  const { form, isFormValid, submit, submitOnPressMetaEnter } = useFormValidator({
    defaultValues,
    onSubmit: async (values) => {
      const hasFullNameChanged = editableBeneficiary.fullName !== values.fullName
      if (!hasFullNameChanged) return
      await dispatch(
        storeActions.beneficiary.update({
          beneficiaryFields: { fullName: values.fullName },
        }),
      )
      void dispatch(
        storeActions.beneficiary.document.create({
          content: t('fullNameUpdated', {
            oldName: editableBeneficiary.fullName,
            newName: values.fullName,
          }),
          type: DocumentType.NOTE,
        }),
      )

      onSubmitted()
    },
    schema: { fullName: beneficiarySchema.fullName },
  })

  const handleReset = () => {
    form.reset(defaultValues)
  }
  return (
    <Form {...form}>
      <form onSubmit={submit} className="space-y-2" onKeyDown={submitOnPressMetaEnter}>
        <FormField
          control={form.control}
          name="fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tc('fullName')}</FormLabel>
              <FormControl>
                <Input placeholder={tc('fullName')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {children}

        <DialogFooter>
          <DeleteButton beneficiaryId={editableBeneficiary.id} />

          <ResetAndUpdate onReset={handleReset} onUpdate={submit} disabled={!isFormValid} />
        </DialogFooter>
      </form>
    </Form>
  )
}

function DeleteButton({ beneficiaryId }: { beneficiaryId: string }) {
  const dispatch = useStoreDispatch()
  const { tc } = useT()
  const view = useView()
  const ability = useAbility()
  const handleDelete = () => {
    void dispatch(
      storeActions.beneficiary.delete({
        beneficiaryId,
      }),
    )
    view.dashboard().navigate()
  }
  if (ability.cannot('delete', 'Beneficiary')) return null
  return (
    <div className="flex flex-1 justify-start">
      <Button type="button" variant="outline" isSensitive onClick={handleDelete}>
        <Trash2Icon className="size-4" />
        {tc('delete', { cfl: true })}
      </Button>
    </div>
  )
}
